import React, { useState, useEffect, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useManualQuery, useMutation } from "graphql-hooks";
import { useNavigate, Link } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";

import {
  TextField,
  Box,
  Divider,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Avatar,
  Select,
  MenuItem,
} from "@mui/material";

import {
  ALL_STORIES_QUERY,
  UPDATE_STORY_STATUS,
  DELETE_STORY,
} from "../graphQL/story_qry";

import AlertPopup from "../components/AlertPopup.js";
import { styled } from "@mui/material/styles";

import {
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  VisibilityOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowDropDown,
} from "@mui/icons-material";

// Import toy images
import photo1 from "../assets/ToysList/photo1.png";
import photo2 from "../assets/ToysList/photo2.png";
import photo3 from "../assets/ToysList/photo3.png";
import photo4 from "../assets/ToysList/photo4.png";
import photo5 from "../assets/ToysList/photo5.png";
import photo6 from "../assets/ToysList/photo6.png";

// Status Options
const STATUS_OPTIONS = ["All", "Published", "Draft"];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

// UserLink Component
const UserLink = ({ username, userpath }) => (
  <StyledTooltip title="View User's Page">
    <Link
      to={`${userpath}/profile`}
      style={{
        color: "red",
        textDecoration: "none",
        fontSize: "14px",
        display: "inline-block",
        lineHeight: 1,
      }}
      target="blank"
      onMouseEnter={e => {
        e.target.style.textDecoration = "underline";
      }}
      onMouseLeave={e => {
        e.target.style.textDecoration = "none";
      }}
    >
      {username}
    </Link>
  </StyledTooltip>
);

const RootDiv = styled("div")({
  marginRight: "8rem",
  textAlign: "center",
  padding: 2,
  paddingLeft: 0,
  paddingRight: 0,
  width: "1400px",
  //  minWidth: "500px",
});

const ContainerDiv = styled("div")({
  padding: 32,
  marginBottom: "5rem",
  clear: "both",
  borderRadius: "8px",
  minHeight: "870px",
  backgroundColor: "#ffffff",
});

export default function AdminStoriesList() {
  const navigate = useNavigate();
  const [isPaginationMenuOpen, setIsPaginationMenuOpen] = useState(false);
  const paginationSelectRef = useRef(null);
  const paginationMenuRef = useRef(null);
  // Alert popup state
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: null,
    storyUuid: null,
  });

  const [statusFilter, setStatusFilter] = useState("All");
  const [stories, setStories] = useState([]);
  const [rows, setRows] = useState([]);

  const [updateStoryStatus] = useMutation(UPDATE_STORY_STATUS, {
    fetchPolicy: "network-only",
  });

  const [deleteStory] = useMutation(DELETE_STORY, {
    fetchPolicy: "network-only",
  });

  const [getAllStoriesData] = useManualQuery(ALL_STORIES_QUERY, {
    fetchPolicy: "network-only",
  });
  const setStoryList = async () => {
    const {
      loading: loadingStoryListData,
      error: errorStoryListData,
      data: storyListData,
    } = await getAllStoriesData();
    if (!loadingStoryListData && storyListData && storyListData.allStories) {
      console.log(storyListData.allStories);
      setStories(
        storyListData.allStories.map(story => ({
          id: story.storyId,
          uuid: story.uuid,
          username: story.userId.username,
          userUuid: story.userId.userUuid,
          fileName: story.fileName,
          title: story.title,
          content: story.content,
          createdOn: dayjs(story.createDate).format("MMM D, YYYY"),
          publishedOn: story.isPublished
            ? dayjs(story.publishedDate).format("MMM D, YYYY")
            : "",
          status: story.isPublished ? "Published" : "Draft",
          //photoName: story.fileName?.split("/").pop(),
          //photoSize: story.fileSize ? (story.fileSize/ 1024).toFixed(0): ""
        }))
      );
    } else {
      console.log("errorCurrStoryData: ", errorStoryListData);
    }
  };

  useEffect(() => {
    (async () => setStoryList())();
  }, []);

  // Initialize data
  useEffect(() => {
    setRows(getFilteredData);
  }, [statusFilter, stories]);

  const getFilteredData = () => {
    let filteredData = stories;
    if (statusFilter !== "All") {
      if (statusFilter === "Draft") {
        filteredData = filteredData.filter(toy => toy.status === "Draft");
      } else {
        filteredData = filteredData.filter(toy => toy.status === "Published");
      }
    }
    console.log(filteredData);
    return filteredData;
  };

  // Status filter change handlers
  const handleStatusFilterChange = event => {
    setStatusFilter(event.target.value);
  };

  const dateFormatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true, // change to false if you prefer 24h format
  });

  const dayFormat = day => {
    if (day) {
      const dayDate = new Date(day);
      return formatter.format(dayDate);
    } else return "";
  };

  const dateFormat = date => {
    if (date) {
      const dayDate = new Date(date);
      return dateFormatter.format(dayDate);
    } else {
      return "";
    }
  };

  // Popup handlers
  const handlePublishClick = storyUuid => {
    setAlertPopup({
      open: true,
      type: "publish",
      storyUuid: storyUuid,
    });
  };

  const handleUnpublishClick = storyUuid => {
    setAlertPopup({
      open: true,
      type: "unpublish",
      storyUuid: storyUuid,
    });
  };

  const handleDeleteClick = storyUuid => {
    setAlertPopup({
      open: true,
      type: "delete",
      storyUuid: storyUuid,
    });
  };

  const handleConfirmAction = async () => {
    if (alertPopup.type === "delete") {
      const {
        loading: loadingStoryDelete,
        error: errorStoryDelete,
        data: storyDelete,
      } = await deleteStory({
        variables: {
          storyUuid: alertPopup.storyUuid,
        },
      });
      if (!loadingStoryDelete && storyDelete && storyDelete.deleteStory) {
        setStories(prevStories =>
          prevStories.filter(story => story.uuid !== alertPopup.storyUuid)
        );
      } else {
        console.log("errorStoryDelete: ", errorStoryDelete);
      }
    } else {
      let isPublished = false;
      if (alertPopup.type === "publish") {
        isPublished = true;
      } else if (alertPopup.type === "unpublish") {
        isPublished = false;
      }
      const {
        loading: loadingStoryStatus,
        error: errorStoryStatus,
        data: storyStatus,
      } = await updateStoryStatus({
        variables: {
          storyUuid: alertPopup.storyUuid,
          isPublished: isPublished,
        },
      });
      if (!loadingStoryStatus && storyStatus && storyStatus.updateStoryStatus) {
        setStories(prevStories =>
          prevStories.map(story =>
            story.uuid === alertPopup.storyUuid
              ? {
                  ...story,
                  publishedOn: isPublished
                    ? new Date().toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "",
                  status: isPublished ? "Published" : "Draft",
                }
              : story
          )
        );
      } else {
        console.log("errorStoryData: ", errorStoryStatus);
      }
    }
    setAlertPopup({ open: false, type: "", storyUuid: null });
  };

  const handleCancelAction = () => {
    setAlertPopup({ open: false, type: "", storyUuid: null });
  };

  const columns = [
    {
      field: "toyImage",
      headerName: "Image",
      minWidth: 5,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Avatar
              src={rows.row.fileName}
              alt={rows.row.title}
              sx={{
                width: 80,
                height: 80,
                borderRadius: "8px",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "username",
      headerName: "Creator",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        let userPath = "/user/" + rows.row.userUuid;
        return <UserLink username={rows.row.username} userpath={userPath} />;
      },
    },
    {
      field: "title",
      headerName: "Title",
      minWidth: 10,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: params => (
        <div
          style={{
            whiteSpace: "normal", // ⬅️ allows wrapping
            wordBreak: "break-word",
            lineHeight: "1.2",
            textAlign: "left",
            padding: "0 4px",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "dateCreated",
      headerName: "Date Created",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <StyledTooltip title={dayFormat(rows.row.createdOn)} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {dateFormat(rows.row.createdOn)}
            </span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "datePublished",
      headerName: "Date Published",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        if (rows.row.datePublished === "") return "";
        return (
          <StyledTooltip title={dayFormat(rows.row.publishedOn)} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {dateFormat(rows.row.publishedOn)}
            </span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 20,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              color: "#333",
              border: "1px solid #000",
              fontSize: "12px",
              fontWeight: "500",
              width: "80px",
              height: "30px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            {rows.row.status}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 1,
      sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {rows.row.status === "Draft" ? (
              <StyledTooltip title="Publish this story">
                <IconButton
                  size="small"
                  onClick={() => handlePublishClick(rows.row.uuid)}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <AssignmentTurnedInOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            ) : (
              <StyledTooltip title="Unpublish this story">
                <IconButton
                  size="small"
                  onClick={() => handleUnpublishClick(rows.row.uuid)}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <FactCheckOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            )}
            <StyledTooltip title="View this story">
              <IconButton
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                  mt: 1,
                }}
              >
                <Link
                  component={RouterLink}
                  to={`/story/${rows.row.uuid}`}
                  target="_blank"
                  color="black"
                  underline="none"
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <VisibilityOutlined fontSize="small" />
                </Link>
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Edit this story">
              <IconButton
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <Link
                  component={RouterLink}
                  to={`/story/${rows.row.uuid}/edit`}
                  target="_blank"
                  color="black"
                  underline="none"
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <EditOutlined fontSize="small" />
                </Link>
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Delete this story">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(rows.row.uuid)}
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
          </Box>
        );
      },
    },
  ];

  function DataGridTitle() {
    return (
      <>
        <Grid
          container
          xs={12}
          sx={{
            pt: 2,
            pb: 2,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: 20,
              fontWeight: 500,
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              margin: "0 auto",
            }}
          >
            Stories List
          </Typography>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            sx={{
              width: "170px",
              paddingRight: 2,
              "& .MuiSelect-select": {
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              },
              "& .MuiOutlinedInput-root": {
                height: "40px",
                backgroundColor: "white",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#666",
                fontSize: "14px",
                "&.Mui-focused": {
                  color: "#666",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            }}
            SelectProps={{
              IconComponent: ArrowDropDown,
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      justifyContent: "flex-start",
                      textAlign: "left",
                    },
                  },
                },
              },
            }}
          >
            {STATUS_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Divider />
      </>
    );
  }

  useEffect(() => {
    const handleScroll = event => {
      if (isPaginationMenuOpen) {
        const isScrollingInsideDropdown =
          (paginationMenuRef.current &&
            paginationMenuRef.current.contains(event.target)) ||
          (paginationSelectRef.current &&
            paginationSelectRef.current.contains(event.target));

        if (!isScrollingInsideDropdown) {
          setIsPaginationMenuOpen(false);
        }
      }
    };

    if (isPaginationMenuOpen) {
      document.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isPaginationMenuOpen]);

  const alertConfig = {
    publish: {
      title: "Draft to Publish",
      message: "Are you ready to publish this story?",
      confirmText: "YES, PUBLISH",
    },
    unpublish: {
      title: "Publish to Draft",
      message: "Are you sure you want to unpublish this story?",
      confirmText: "YES, UNPUBLISH",
    },
    delete: {
      title: "Delete Story",
      message: "Are you sure you want to delete this story?",
      confirmText: "YES, DELETE",
    },
  };

  return (
    <RootDiv>
      <title>R3PLAY - Report Management – Admin</title>
      <ContainerDiv>
        <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
              position: "relative",
              zIndex: 1,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            // Firefox-specific rendering fix
            "@-moz-document url-prefix()": {
              "& .MuiDataGrid-columnHeaders": {
                transform: "translateY(0.1px)",
              },
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: 12,
            },
          }}
          columns={columns}
          rows={rows}
          showCellRightBorder={true}
          slots={{ toolbar: DataGridTitle }}
          getRowId={row => row.id}
          autoHeight={true}
          rowHeight={90}
          experimentalFeatures={{ newEditingApi: true }}
          pagination
          pageSizeOptions={[10, 20, 50]} // user can select page size
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } }, // default to 10 rows
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          slotProps={{
            pagination: {
              SelectProps: {
                open: isPaginationMenuOpen,
                onOpen: () => setIsPaginationMenuOpen(true),
                onClose: () => setIsPaginationMenuOpen(false),
                ref: paginationSelectRef,
                MenuProps: {
                  PaperProps: {
                    ref: paginationMenuRef,
                  },
                },
              },
            },
          }}
        />
        {/* Alert Popup */}
        {alertPopup.type && (
          <AlertPopup
            open={alertPopup.open}
            onClose={handleCancelAction}
            {...alertConfig[alertPopup.type]}
            onConfirm={handleConfirmAction}
          />
        )}
      </ContainerDiv>
    </RootDiv>
  );
}
